import React, { useState } from "react";
import { IoCaretDown, IoCheckmark, IoCreateOutline } from "react-icons/io5";
import { useUser } from "../../../providers/UserProvider";
import { User } from "../../../storage/store";
import { UserService } from "../../../services/UserService";
import { apiMessage } from "../../../api/apiMessage";
import { useToggleTop } from "../../common/ToggleTop";
import { placesChanged } from "../../places/places";
import Sheet from "./Sheet";
import NameDialog from "./NameDialog";
import AlertDialog from "./AlertDialog";

const initial = (name) => (name ? name.trim().charAt(0) : "?");

// The house button next to the greeting and its Select House sheet
// (app/screens/FirstScreen.js). Its pencil lists each home's Rename (owners)
// and Delete (the user who created it, once it has no devices, rooms or floors).
const PropertyPicker = () => {
  const { profileName, selectProperty, selectedPropertey } = useUser();
  const toast = useToggleTop();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState(null);
  const selectedId = User.getPropertyId();
  const selected = profileName.find((house) => house.id === selectedId) ?? profileName[0];

  const close = () => {
    setOpen(false);
    setEditing(false);
  };

  const choose = (house) => {
    close();
    if (house.id !== selectedId) selectProperty(house.id);
  };

  // As the app does: the home's config with its new name, saved whole.
  const rename = async (name) => {
    const house = renaming;
    setSaving(true);
    try {
      const config = await UserService.getConfig(house.id);
      const response = await UserService.updateConfig({ ...config, name }, house.id);
      if (response !== "Updated successfully") throw new Error(String(response));
      setRenaming(null);
      toast("Home name updated", "green");
      selectedPropertey();
    } catch (error) {
      toast(apiMessage(error, "Couldn't rename the home. Please try again."), "red");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (house) => {
    setDialog(null);
    try {
      await UserService.deleteProperty(house.id);
    } catch (error) {
      toast(apiMessage(error, "Couldn't delete the home. Please try again."), "red");
      return;
    }
    toast(`"${house.name}" deleted`, "green");
    if (house.id === selectedId) {
      // Move to another home, or to none when it was the last one.
      let left = [];
      try {
        left = await UserService.getProperty();
      } catch (error) {
        // No homes left: the API answers 404.
      }
      const next = Array.isArray(left) ? left.find((item) => item.property?.id && item.property.id !== house.id) : null;
      if (next) selectProperty(next.property.id);
      else User.removePropertyId();
    }
    await selectedPropertey();
    placesChanged();
  };

  // A home can go only when it's empty, as in the app.
  const askRemove = async (house) => {
    try {
      const config = await UserService.getConfig(house.id);
      const rooms = (config?.floors ?? []).reduce((count, floor) => count + (floor.rooms?.length ?? 0), 0);
      if (config?.devices?.length || config?.floors?.length || rooms) {
        setDialog({
          type: "error",
          title: "Delete Home",
          message: `First remove all devices, rooms and floors from "${house.name}".`,
        });
        return;
      }
    } catch (error) {
      toast(apiMessage(error, "Couldn't check the home. Please try again."), "red");
      return;
    }
    setDialog({
      type: "confirmation",
      title: "Delete Home",
      message: `Are you sure you want to delete the home "${house.name}"?`,
      confirmLabel: "Delete",
      onConfirm: () => remove(house),
    });
  };

  return (
    <>
      <button
        type="button"
        className="hx-house-pill"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label={`Change house. Current: ${selected?.name ?? "none"}`}
      >
        <span className="hx-avatar" aria-hidden="true">
          {initial(selected?.name)}
        </span>
        <IoCaretDown aria-hidden="true" />
      </button>

      <Sheet
        open={open}
        onClose={close}
        title="Select House"
        actions={
          profileName.length ? (
            <button
              type="button"
              className={`hx-icon-btn ${editing ? "is-active" : ""}`}
              aria-pressed={editing}
              aria-label={editing ? "Done editing homes" : "Edit homes"}
              onClick={() => setEditing(!editing)}
            >
              <IoCreateOutline />
            </button>
          ) : null
        }
      >
        <div className="hx-houses">
          {profileName.map((house) => {
            const isSelected = house.id === selected?.id;
            const avatar = (
              <span className="hx-avatar is-large" aria-hidden="true">
                {initial(house.name)}
                {isSelected ? (
                  <span className="hx-house-check">
                    <IoCheckmark />
                  </span>
                ) : null}
              </span>
            );
            if (editing) {
              const canRename = house.role === "owner";
              return (
                <div key={house.id} className="hx-house is-editing">
                  {avatar}
                  {house.name}
                  <span className="hx-house-edit">
                    {canRename ? (
                      <button type="button" onClick={() => setRenaming(house)}>
                        Rename
                      </button>
                    ) : null}
                    {house.superOwner ? (
                      <button type="button" className="is-danger" onClick={() => askRemove(house)}>
                        Delete
                      </button>
                    ) : null}
                    {!canRename && !house.superOwner ? <span>Guest</span> : null}
                  </span>
                </div>
              );
            }
            return (
              <button
                key={house.id}
                type="button"
                className={`hx-house ${isSelected ? "is-selected" : ""}`}
                onClick={() => choose(house)}
                aria-pressed={isSelected}
              >
                {avatar}
                {house.name}
              </button>
            );
          })}
        </div>
      </Sheet>

      <NameDialog
        open={Boolean(renaming)}
        idPrefix="hx-rename-home"
        title="Edit Name"
        label="Home name"
        initial={renaming?.name ?? ""}
        submitLabel="Save"
        busy={saving}
        onClose={() => setRenaming(null)}
        onSubmit={rename}
      />
      <AlertDialog
        open={Boolean(dialog)}
        type={dialog?.type}
        title={dialog?.title}
        message={dialog?.message}
        confirmLabel={dialog?.confirmLabel}
        onClose={() => setDialog(null)}
        onConfirm={dialog?.onConfirm}
      />
    </>
  );
};

export default PropertyPicker;
